import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SubscriptionManager.css';

const SubscriptionManager = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/subscriptions/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/subscriptions/create-portal-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You\'ll keep access until the end of your billing period.')) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/subscriptions/cancel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Your subscription will be canceled on ${new Date(data.cancelAt).toLocaleDateString()}`);
        fetchSubscriptionStatus();
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/subscriptions/resume', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Your subscription has been resumed!');
        fetchSubscriptionStatus();
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
      alert('Failed to resume subscription. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTierName = (tier) => {
    switch (tier) {
      case 'free':
        return 'WanderVibe Free';
      case 'plus':
        return 'WanderVibe Plus';
      case 'pro':
        return 'WanderVibe Pro';
      default:
        return tier;
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Free Trial';
      case 'canceled':
        return 'Canceled';
      case 'past_due':
        return 'Past Due';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'trialing':
        return 'blue';
      case 'canceled':
        return 'red';
      case 'past_due':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <div className="subscription-manager loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const isFree = subscription?.tier === 'free';
  const isTrialing = subscription?.status === 'trialing';
  const isCanceled = subscription?.cancelAtPeriodEnd;

  return (
    <div className="subscription-manager">
      <h2>Subscription & Billing</h2>

      <div className="subscription-card">
        <div className="subscription-header">
          <div>
            <h3>{formatTierName(subscription?.tier)}</h3>
            <span className={`status-badge ${getStatusColor(subscription?.status)}`}>
              {formatStatus(subscription?.status)}
            </span>
          </div>

          {!isFree && (
            <div className="subscription-actions-header">
              <button
                className="btn-manage"
                onClick={handleManageBilling}
                disabled={actionLoading}
              >
                Manage Billing
              </button>
            </div>
          )}
        </div>

        <div className="subscription-details">
          {isFree ? (
            <div className="free-tier-message">
              <p>You're currently on the free plan. Upgrade to unlock more features!</p>
              <button
                className="btn-upgrade"
                onClick={() => navigate('/pricing')}
              >
                View Plans
              </button>
            </div>
          ) : (
            <>
              {isTrialing && subscription.trialEnd && (
                <div className="trial-notice">
                  <svg className="info-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M12 16v-4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <span>
                    Your free trial ends on {new Date(subscription.trialEnd).toLocaleDateString()}
                  </span>
                </div>
              )}

              {subscription.currentPeriodEnd && (
                <div className="detail-row">
                  <span className="detail-label">
                    {isCanceled ? 'Access until:' : 'Next billing date:'}
                  </span>
                  <span className="detail-value">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </div>
              )}

              {isCanceled && (
                <div className="cancellation-notice">
                  <svg className="warning-icon" viewBox="0 0 24 24" fill="none">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <div>
                    <p className="notice-title">Subscription Canceled</p>
                    <p className="notice-text">
                      Your subscription will end on {new Date(subscription.currentPeriodEnd).toLocaleDateString()}.
                      You can resume anytime before then.
                    </p>
                  </div>
                </div>
              )}

              <div className="subscription-actions">
                {isCanceled ? (
                  <button
                    className="btn-resume"
                    onClick={handleResumeSubscription}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : 'Resume Subscription'}
                  </button>
                ) : (
                  <>
                    <button
                      className="btn-upgrade"
                      onClick={() => navigate('/pricing')}
                    >
                      {subscription.tier === 'plus' ? 'Upgrade to Pro' : 'Change Plan'}
                    </button>

                    <button
                      className="btn-cancel"
                      onClick={handleCancelSubscription}
                      disabled={actionLoading}
                    >
                      {actionLoading ? 'Processing...' : 'Cancel Subscription'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {!isFree && (
        <div className="billing-info">
          <h3>Billing Information</h3>
          <p>
            To update your payment method, view invoices, or manage your billing details,
            click "Manage Billing" above.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;
